import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage, legalHead } from "@/components/LegalPage";
import { RECOVERY_TIERS } from "@/components/SecurityBlocks";
import { services } from "@/data/services";
import { ORG_ID, WEBSITE_ID, SITE } from "@/lib/seo";

const TITLE = "About Preserve Earth | Secure Textile Recycling UK";
const DESCRIPTION =
  "Preserve Earth, a trading name of RBM London Limited, serves businesses across the UK needing secure textile recycling and confidential waste disposal.";

export const Route = createFileRoute("/about")({
  head: () => {
    const base = legalHead({ title: TITLE, description: DESCRIPTION, path: "/about", crumbName: "About" });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "@id": `${SITE}/about#webpage`,
            url: `${SITE}/about`,
            name: TITLE,
            description: DESCRIPTION,
            inLanguage: "en-GB",
            isPartOf: { "@id": WEBSITE_ID },
            about: { "@id": ORG_ID },
            mainEntity: { "@id": ORG_ID },
          }),
        },
        ...base.scripts,
      ],
    };
  },
  component: AboutPage,
});

const steps = [
  ["Collection", "Discreet, secure collection in unmarked, GPS-tracked vehicles with CCTV."],
  ["Verified handling", "Handled by fully vetted staff, with a full chain of custody from collection to outcome."],
  ["Secure shredding", "Items are shredded beyond recognition at secure facilities."],
  ["Recovery", "Shredded material follows the recovery tiers set out below."],
  [
    "Audit record",
    "Every job gets an electronic note of secure destruction. Clients can opt for an Environmental Impact Statement showing the CO2e savings.",
  ],
];

const h2 = "font-serif text-2xl text-charcoal md:text-3xl";
const link = "text-moss underline decoration-moss/40 underline-offset-4 hover:decoration-moss";

function AboutPage() {
  return (
    <LegalPage
      crumb={{ name: "About", path: "/about" }}
      eyebrow="About"
      title="About Preserve Earth."
      showPolicyContact={false}
      intro={null}
    >
      <div className="mt-4 grid max-w-3xl gap-14 text-base leading-relaxed text-charcoal/75">
        <section aria-labelledby="who">
          <h2 id="who" className={h2}>Who Preserve Earth is</h2>
          <p className="mt-4 text-pretty">
            Preserve Earth is a trading name of RBM London Limited, Company No. 15482817, serving
            businesses across the UK with secure, sustainable disposal.
          </p>
        </section>

        <section aria-labelledby="what">
          <h2 id="what" className={h2}>Services</h2>
          <ul className="mt-4 list-disc space-y-2 pl-5 marker:text-moss">
            {services.map((s) => (
              <li key={s.slug}>
                <Link to="/services/$slug" params={{ slug: s.slug }} className={link}>
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="how">
          <h2 id="how" className={h2}>How a collection works</h2>
          <ol className="mt-4 list-decimal space-y-3 pl-5 marker:text-moss">
            {steps.map(([t, b]) => (
              <li key={t}>
                <strong className="font-medium text-charcoal">{t}.</strong> {b}
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="recovery">
          <h2 id="recovery" className={h2}>Recovery commitments</h2>
          <ol className="mt-4 list-decimal space-y-3 pl-5 marker:text-moss">
            {RECOVERY_TIERS.map((t) => (
              <li key={t.title}>
                <strong className="font-medium text-charcoal">{t.title}.</strong> {t.body}
              </li>
            ))}
          </ol>
          <p className="mt-4">The commitment is 100% landfill diversion.</p>
        </section>

        <section aria-labelledby="enquire-cta" className="border-t border-charcoal/10 pt-10">
          <h2 id="enquire-cta" className={h2}>Enquire about a collection</h2>
          <a
            href="/#enquire"
            className="mt-6 inline-flex items-center justify-center rounded-sm bg-primary px-6 py-3.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Make an enquiry
          </a>
        </section>
      </div>
    </LegalPage>
  );
}
